import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import theme from '../../../../assets/theme';
import { useDesignerUIContext } from '../../../../hooks/useDesignerUIState';
import { useMetrics } from '../../../../hooks/useMetrics';
import { Button } from '../../../_atoms/Button';
import { getFirstSlateElementPropsFromSlateString } from '../../../SlideComponents/slateUtil';
import { Slide } from '../../../SlideComponents/slide.types';
import {
  createTextElement,
  isSlideElementText,
  SLIDE_ASPECT,
} from '../../../SlideComponents/slideUtil';

const TextTab = ({
  onRoundChange,
  selectedSlide,
}: {
  onRoundChange: (round: Slide) => void;
  selectedSlide?: Slide;
}) => {
  const { t } = useTranslation('Host');
  const metrics = useMetrics();
  const setSelectedElements = useDesignerUIContext(
    context => context.setSelectedElements,
  );
  const onCloseDrawer = useDesignerUIContext(context => context.handleCloseDrawer);
  // the default color is the
  const colorToUse = useMemo(() => {
    if (!selectedSlide?.backgroundMedia) return theme.colors.blackPure;
    const textElements = selectedSlide?.elements?.filter(e => isSlideElementText(e));
    if (!textElements || textElements.length === 0) return 'white';
    const lastElement = textElements[textElements.length - 1];
    const slateProps = getFirstSlateElementPropsFromSlateString(lastElement.textValue);
    return slateProps.color || 'white';
  }, [selectedSlide?.backgroundMedia, selectedSlide?.elements]);

  const onTextAdded = ({
    fontSize,
    widthPercentage,
    textValue,
    slateTextValue,
  }: {
    fontSize: number;
    widthPercentage: number;
    textValue?: string;
    slateTextValue?: string;
  }) => {
    if (!selectedSlide) return;
    metrics.logEvent('Creator.TextTab.Add', { fontSize: fontSize });
    const newElement = createTextElement({
      fontSize,
      width: SLIDE_ASPECT.width * widthPercentage,
      textValue,
      textColor: colorToUse,
      slateTextValue,
    });

    setSelectedElements([]);

    // THIS IS A HACK TO MAKE SURE THE ELEMENT IS RENDERED BEFORE SELECTING IT
    // This prevents Moveable from starting to drag the selected Element
    setTimeout(() => {
      onRoundChange({
        ...selectedSlide,
        elements: [...(selectedSlide?.elements || []), newElement],
      });

      setSelectedElements([newElement]);
      onCloseDrawer();
    }, 100);
  };
  return (
    <Container>
      <Title $fontSize={20}>{t('Headers and text')}</Title>
      <ItemsContainer>
        <TextButton
          $fontSize={100 / 3}
          type="ghost"
          onClick={() =>
            onTextAdded({
              fontSize: 100,
              widthPercentage: 0.9,
              textValue: '',
            })
          }
        >
          Header text
        </TextButton>
        <TextButton
          $fontSize={70 / 3}
          type="ghost"
          onClick={() =>
            onTextAdded({
              fontSize: 70,
              widthPercentage: 0.9,
              textValue: '',
            })
          }
        >
          Sub header text
        </TextButton>
        <TextButton
          $fontSize={40 / 3}
          type="ghost"
          onClick={() =>
            onTextAdded({
              fontSize: 40,
              widthPercentage: 0.3,
              textValue: '',
            })
          }
        >
          Normal text
        </TextButton>
        <TextButton
          $fontSize={60 / 3}
          type="ghost"
          onClick={() =>
            onTextAdded({
              fontSize: 40,
              widthPercentage: 0.3,
              slateTextValue: `[{"type":"bulleted-list","children":[{"type":"list-item","children":[{"text":""}],"align":"left","size":100,"color":"${colorToUse}"}],"size":85,"color":"#${colorToUse}"}]`,
            })
          }
        >
          <ul>
            <li>Bullet </li>
            <li>Bullet </li>
            <li>Bullet </li>
          </ul>
        </TextButton>
        <TextButton
          $fontSize={60 / 3}
          type="ghost"
          onClick={() =>
            onTextAdded({
              fontSize: 40,
              widthPercentage: 0.3,
              slateTextValue: `[{"type":"numbered-list","children":[{"type":"list-item","children":[{"text":""}],"align":"left","size":100,"color":"${colorToUse}"}],"size":85,"color":"#${colorToUse}"}]`,
            })
          }
        >
          <ol>
            <li>Number </li>
            <li>Number</li>
            <li>Number </li>
          </ol>
        </TextButton>
      </ItemsContainer>
    </Container>
  );
};

const Container = styled.div`
  padding: 20px 20px 0px 20px;
  width: 100%;
  height: 100%;
`;
const ItemsContainer = styled.div`
  width: 100%;
  padding-top: 20px;
  padding-bottom: 40px;
  height: calc(100% - 28px);
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
`;
const TextButton = styled(Button)<{ $fontSize: number }>`
  font-size: ${({ $fontSize }) => $fontSize}px;
  width: 100%;
  padding-left: 0;
  background-color: transparent;
  justify-content: flex-start;
  display: flex;
  border: none;
  border-radius: 0px;
  padding: 20px 10px;
  box-shadow: inset 0px -1px 0px ${theme.colors.greyLight};
  ol,
  ul {
    transform: translateX(25%);
    padding: 0;
    margin: 0;
  }
`;
const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: 140%;
  color: ${theme.colors.greyVeryDark};
  display: flex;
  align-items: center;
  margin-bottom: 0px;

  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 5}px;
  }
`;

export default TextTab;
